import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable, Injector } from "@angular/core";
import { BroadcastService } from "@bbwt/modules/broadcasting";
import { BaseDataService, extractError, HttpResponsesHandlersFactory } from "@bbwt/modules/data-service";
import { CookieService } from "@main/cookie/cookie.service";
import { PwaSettings, SettingsSectionsName, SystemConfigurationService } from "@main/system-configuration";
import * as moment from "moment";
import { Subject, Subscription, takeUntil } from "rxjs";
import { Access, AnyoneRole, cacheDataGroups, UrlAccess } from "./cache-data-groups";
import { CACHE_COMPLETE, CACHE_COMPONENT_READY, CACHE_DATAGROUP_START, CACHE_URL_END, CACHE_URL_START } from "./cache-event-names";
import { environment } from "@environments/environment";
import { DatagroupStart, UrlEnd, UrlStart } from "./cache-types";
import { UserHelperService } from "@main/users/user-helper.service";
import { AppOnlineStateService } from "../app-online-state.service";
import { AppVersionService } from "@main/app-versioning/api/app-version.service";

@Injectable({
    providedIn: "root"
})
export class AppPwaCacheService extends BaseDataService {
    url: string;
    private static readonly LAST_CACHE_AUTORUN_COOKIE = "MiProLastCacheAutorun";

    private isOnline = false;
    private failedUrls: UrlAccess[] = [];
    private destroyed$ = new Subject<void>();
    private claimsReady = false;
    private cacheComponentReady = false;
    private isPwaCacheFragment = false;
    private appOnlineSubs: Subscription;

    constructor(
        private systemConfiguration: SystemConfigurationService,
        private broadcastService: BroadcastService,
        private userHelperService: UserHelperService,
        private injector: Injector,
        appOnlineService: AppOnlineStateService,
        appVersionService: AppVersionService,
        http: HttpClient,
        handlersFactory: HttpResponsesHandlersFactory
    ) {
        super(http, handlersFactory);

        this.isOnline = appOnlineService.isAppOnline;
        this.isPwaCacheFragment = window.location.hash === "#pwacache";

        this.listen(UserHelperService.ClaimsUpdatedEventName, () => {
            this.claimsReady = true;
            this.setupCache();
        });
        this.listen(CACHE_COMPONENT_READY, () => {
            this.cacheComponentReady = true;
            this.setupCache();
        });

        this.appOnlineSubs = this.broadcastService
            .on<{ isAppOnline: boolean }>(AppOnlineStateService.AppOnlineStateChanged)
            .subscribe(state => this.handleNetworkChange(state.isAppOnline));

        // Remove the last cache setup date to force the app to register them again after reload.
        appVersionService.registerUpdateActions([_ => this.resetCacheAutorun()])
    }

    initialize() {
        // No-Op: only to force construction of this service
    }

    private setupCache() {
        if (!this.claimsReady || !this.cacheComponentReady) {
            return;
        }

        const doCaching =
            environment.production &&
            this.isOnline &&
            this.systemConfiguration.pwaEnabled &&
            (this.isPwaCacheFragment || this.shouldRefreshUrls);

        if (doCaching) {
            this.cache();
        }

        this.stopListening();
    }

    private async cache(): Promise<void> {
        const isAllowedToCache = this.buildIsAllowedToCache();
        AppPwaCacheService.logLine("We are caching some data for OFFLINE.");

        for (const dataGroup of cacheDataGroups) {
            if (!isAllowedToCache(dataGroup.access)) {
                continue;
            }

            this.broadcastService.broadcast(CACHE_DATAGROUP_START, <DatagroupStart>{
                header: dataGroup.header
            });
            AppPwaCacheService.logLine(`${dataGroup.header}.`);

            if (Array.isArray(dataGroup.urls)) {
                await this.processPlainUrls(dataGroup.name, dataGroup.urls, isAllowedToCache);
            }

            dataGroup.buildUrls ??= (url) => [url];

            if (Array.isArray(dataGroup.templUrls) && typeof dataGroup.buildUrls === "function") {
                for (const templUrlAccess of dataGroup.templUrls) {
                    const [templUrl, access] = AppPwaCacheService.splitUrlAccess(templUrlAccess);
                    if (!isAllowedToCache(access)) {
                        continue;
                    }

                    const urls = dataGroup.buildUrls(templUrl, this.injector);
                    await this.processPlainUrls(dataGroup.name, urls, isAllowedToCache);
                }
            }
        }

        AppPwaCacheService.logLine("Complete!");
        this.broadcastService.broadcast(CACHE_COMPLETE);
        CookieService.SetPermanentCookie(AppPwaCacheService.LAST_CACHE_AUTORUN_COOKIE, moment().toJSON());
    }

    private async processPlainUrls(dataGroupName: string, urlAccesses: UrlAccess[], isAllowedToCache: (urlAccess: Access) => boolean) {
        for (const urlAccess of urlAccesses) {
            const [url, access] = AppPwaCacheService.splitUrlAccess(urlAccess);
            if (!isAllowedToCache(access)) {
                continue;
            }

            if (!this.isOnline) {
                this.saveFailedUrl(urlAccess);
                continue;
            }

            const name = `${dataGroupName}_${url}`;
            try {
                this.broadcastService.broadcast(CACHE_URL_START, <UrlStart>{ name, url: url });

                await this.handleRequest(this.http.get(url));

                this.broadcastService.broadcast(CACHE_URL_END, <UrlEnd>{ name, type: "✔️" });
                AppPwaCacheService.logLine(`${url} ✔️.`);
            } catch (err: unknown) {
                this.saveFailedUrl(urlAccess);

                let errorMessage = "An unknown error has occurred";
                if (err instanceof HttpErrorResponse) {
                    errorMessage = extractError(err, errorMessage);
                }

                this.broadcastService.broadcast(CACHE_URL_END, <UrlEnd>{ name, type: "❌", errorMessage });

                AppPwaCacheService.logLine(`${url} - ${errorMessage} ❌.`);
            }
        }
    }

    private saveFailedUrl(url: UrlAccess) {
        if (!this.isOnline) {
            this.failedUrls.push(url);
        }
    }

    private handleNetworkChange(isOnline: boolean) {
        this.isOnline = isOnline;
        if (!isOnline) {
            return;
        }

        if (this.failedUrls.length > 0) {
            AppPwaCacheService.logLine("Retrying failed URLs");
            this.retryFailedUrls();
        } else {
            this.appOnlineSubs?.unsubscribe();
        }
    }

    private async retryFailedUrls() {
        const failedUrls = this.failedUrls;
        this.failedUrls = [];
        const isAllowedToCache = this.buildIsAllowedToCache();
        await this.processPlainUrls("Failed URLs", failedUrls, isAllowedToCache);
    }

    private listen(key: string, handler: () => void) {
        this.broadcastService.on(key).pipe(takeUntil(this.destroyed$)).subscribe(handler);
    }

    private stopListening() {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    private get shouldRefreshUrls() {
        const autoCacheDays = this.autoCacheDaysSetting ?? 10;
        const defaultOlderDate = moment()
            .add(-2 * autoCacheDays, "days")
            .toJSON();
        const lastCacheDate = moment(CookieService.GetCookie(AppPwaCacheService.LAST_CACHE_AUTORUN_COOKIE) ?? defaultOlderDate);
        return lastCacheDate.isBefore(moment().add(-autoCacheDays, "days"));
    }

    private get autoCacheDaysSetting() {
        return this.systemConfiguration.getSettingsSection<PwaSettings>(SettingsSectionsName.PwaSettings)?.autoCacheIntervalInDays;
    }

    private buildIsAllowedToCache() {
        const userRoles = [AnyoneRole, ...(this.userHelperService.roles ?? [])];
        const userPermissions = this.userHelperService.permissions ?? [];
        const userMiproModules = this.userHelperService.enabledModules ?? [];

        return (access: Access) => {
            if (access == null) {
                return true;
            }

            const isIntersection = (allowed: string[], actual: string[]) => actual.some(value => allowed?.includes(value));

            return (
                (isIntersection(access?.roles, userRoles) || isIntersection(access?.permissions, userPermissions)) &&
                ((access?.miproModules ?? []).length === 0 || isIntersection(access?.miproModules, userMiproModules))
            );
        };
    }

    private static splitUrlAccess(urlAccess: UrlAccess): [string, Access] {
        if (typeof urlAccess === "string") {
            return [urlAccess, null];
        }

        return urlAccess;
    }

    private static logLine(line: string) {
        const logTime = moment().format("HH:mm:ss.SSS");
        // eslint-disable-next-line no-console
        console.log(`[${logTime}] [AUTO CACHE] ${line}`);
    }

    private resetCacheAutorun(): void {
        CookieService.DeleteCookie(AppPwaCacheService.LAST_CACHE_AUTORUN_COOKIE);
    }
}
